export type IdentifierLookupState =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "found"; name: string }
  | { status: "notFound" }
  | {
      status: "vatGroup";
      companies: { name?: string; regNo?: string }[];
      hasRegNo: boolean;
    }
  | { status: "companyNumberTooLong" }
  | { status: "vatTooLong" };

// Creditsafe rejects VAT number searches longer than this
export const CREDITSAFE_VAT_MAX_LENGTH = 11;

export const normaliseVatNumber = (value: string) =>
  value.replace(/[\s.-]/g, "").toUpperCase();

export const isLikelyCompanyNumber = (value: string): boolean => {
  const stripped = value.replace(/\s/g, "");
  return /^[A-Z0-9]{6,8}$/i.test(stripped);
};

export const isLikelyVatNumber = (value: string): boolean =>
  /^(GB)?\d{9}$/.test(normaliseVatNumber(value));

export const isVatNumberTooLong = (value: string): boolean =>
  normaliseVatNumber(value).length > CREDITSAFE_VAT_MAX_LENGTH;

const normaliseRegNo = (value?: string) => {
  const regNo = (value ?? "").replace(/\s/g, "").toUpperCase();
  return /^\d+$/.test(regNo) ? regNo.padStart(8, "0") : regNo;
};

const normaliseCompanyName = (value?: string) =>
  (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\blimited\b/g, "ltd")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");

// A VAT search returns every company in a VAT group, so pick the profile's own
// company by registration number, else by name
export const pickVatMatch = <T extends { name?: string; regNo?: string }>(
  companies: T[],
  regNo?: string,
  name?: string,
): T | undefined => {
  if (companies.length <= 1) {
    return companies[0];
  }
  const checks: [
    keyof T & ("regNo" | "name"),
    (v?: string) => string,
    string?,
  ][] = [
    ["regNo", normaliseRegNo, regNo],
    ["name", normaliseCompanyName, name],
  ];
  for (const [key, normalise, value] of checks) {
    const wanted = normalise(value);
    if (!wanted) {
      continue;
    }
    const matches = companies.filter(
      (company) => normalise(company[key]) === wanted,
    );
    if (matches.length === 1) {
      return matches[0];
    }
  }
  return undefined;
};

type CreditsafeProfileFields = {
  vatNo?: string;
  vatNoSkipped?: boolean;
  creditsafeCompanyId?: string;
  creditsafe?: {
    isAvailable?: boolean;
    matchedBy?: "vatNo" | "regNo";
    matchedNumber?: string;
    companyId?: string;
    company?: { id?: string; name?: string | null };
    vatNo?: string;
  };
};

export const isCreditsafeVatVerified = (
  businessProfile?: CreditsafeProfileFields | null,
) => {
  const check = businessProfile?.creditsafe;
  const vatNo = businessProfile?.vatNo;
  if (
    businessProfile?.vatNoSkipped ||
    !vatNo ||
    !check?.isAvailable ||
    !check.company?.name
  ) {
    return false;
  }
  if (check.matchedBy === "vatNo" && check.matchedNumber === vatNo) {
    return true;
  }
  // Legacy soft-compat: pre-unification nested shape used vatNo as the lookup key.
  if (!check.matchedBy && check.vatNo === vatNo) {
    return true;
  }
  return false;
};

export const getCreditsafeCompanyId = (
  businessProfile?: CreditsafeProfileFields | null,
) =>
  businessProfile?.creditsafe?.companyId ||
  businessProfile?.creditsafe?.company?.id ||
  businessProfile?.creditsafeCompanyId;
